import React, { useEffect, useRef, useState } from 'react';
import { allLocales, type Locale } from '@utils/models';
import {
  alpha,
  IconButton,
  Menu,
  MenuItem,
  styled,
  type Theme,
  Tooltip,
  tooltipClasses,
  type TooltipProps,
  Typography,
  useTheme,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';

interface LocaleSwitcherProps {
  className?: string;
  disabled?: boolean;
  value: Locale;
  setLocale: (locale: Locale) => void;
  onLocaleChange?: (locale: Locale) => void;
}

const LOCALE_DISPLAY_NAMES: Record<Locale, string> = {
  'ar-AE': 'العربية (الإمارات العربية المتحدة)',
  'en-GB': 'English (UK)',
  'en-US': 'English (US)',
  'es-US': 'Español (Estados Unidos)',
  'fi-FI': 'Suomi (Suomi)',
  'fr-BE': 'Français (Belgique)',
  'nb-NO': 'Norsk (Norge)',
  'nl-BE': 'Nederlands (België)',
  'pt-BR': 'Português (Brasil)',
  'sv-SE': 'Svenska (Sverige)',
  'it-IT': 'Italiano (Italia)',
};

const LocaleSwitcherContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  padding: 8px;
  transition: all 0.2s ease-in-out;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .MuiSvgIcon-root {
    color: white;
  }
`;

const StyledMenuItem = styled(MenuItem)<{ theme: Theme }>`
  font-size: 0.875rem;
  padding: 8px 16px;
  min-height: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  .tick-icon {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 1rem;
  }
  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
    .tick-icon {
      opacity: 0.6;
    }
  }
  &.Mui-selected {
    background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.08)};
    font-weight: 500;
    &:hover {
      background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};
    }
    .tick-icon {
      opacity: 1;
    }
  }
`;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))`
  & .${tooltipClasses.tooltip} {
    background-color: ${({ theme }) => theme.palette.common.white};
    color: rgba(0, 0, 0, 0.87);
    box-shadow: ${({ theme }) => theme.shadows[1]};
    font-size: 11px;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
  }
  & .${tooltipClasses.arrow} {
    color: ${({ theme }) => theme.palette.common.white};
    &::before {
      border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    }
  }
`;

const SelectedLocaleText = styled(Typography)`
  color: white;
  font-size: 0.875rem;
  margin-left: 8px;
  font-weight: 400;
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  &.disabled {
    opacity: 0.5;
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    margin-top: 8px;
    min-width: 200px;
    max-height: 300px;
    overflow: auto;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
  }
`;

export const LocaleSwitcher = ({
  className = '',
  disabled = false,
  value,
  setLocale,
  onLocaleChange,
}: LocaleSwitcherProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTextTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();
    const timeoutId = setTimeout(checkTruncation, 100);

    return () => clearTimeout(timeoutId);
  }, [value]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLocaleChange = (selectedLocale: Locale) => {
    setLocale(selectedLocale);
    if (onLocaleChange) onLocaleChange(selectedLocale);
    handleClose();
  };

  const displayName = LOCALE_DISPLAY_NAMES[value];

  return (
    <LocaleSwitcherContainer className={className}>
      <StyledIconButton
        onClick={handleClick}
        disabled={disabled}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <LanguageIcon />
        <StyledTooltip
          title={isTextTruncated ? displayName : ''}
          arrow
          placement="bottom"
          disableHoverListener={!isTextTruncated}
        >
          <SelectedLocaleText ref={textRef} className={disabled ? 'disabled' : ''}>
            {displayName}
          </SelectedLocaleText>
        </StyledTooltip>
      </StyledIconButton>

      <StyledMenu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {allLocales.map((locale) => (
          <StyledMenuItem
            theme={theme}
            key={locale}
            selected={locale === value}
            onClick={() => handleLocaleChange(locale)}
          >
            <span>{LOCALE_DISPLAY_NAMES[locale]}</span>
            <CheckIcon className="tick-icon" />
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </LocaleSwitcherContainer>
  );
};
